/**
 * useCharlotteVoice — Voice chat hook for Charlotte AI assistant.
 *
 * Ported from @netrun-crm/kog-frontend, made generic:
 *  - Config passed via parameters (no hardcoded URLs)
 *  - No KOG-specific references
 *  - Keeps: WebSocket audio streaming, PCM encoding, transcript handling, engagement signals
 *
 * Manages a Gemini Live WebSocket session with bidirectional PCM audio streaming.
 * Captures mic audio, resamples to 24 kHz PCM16, streams to Charlotte backend.
 * Receives audio responses and plays them via AudioContext queue.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

// ── Types ────────────────────────────────────────────────────────────

export interface CharlotteVoiceConfig {
  /** Charlotte API URL (HTTP). WS URL is derived by replacing http with ws + /api/voice/voicelive/ws */
  apiUrl: string;
  /** Direct WebSocket URL override (skips apiUrl derivation) */
  wsUrl?: string;
  /** TTS voice name */
  voice?: string;
  /** System instructions */
  instructions?: string;
  /** RAG collection for context */
  ragCollection?: string;
}

export interface VoiceTranscript {
  id: string;
  source: 'user' | 'assistant';
  text: string;
  timestamp: number;
}

export interface VoiceStats {
  audioChunksSent: number;
  audioChunksReceived: number;
  responsesCount: number;
}

export interface VoiceEngagement {
  signal: string;
  register: string;
  satisfaction_total: number;
  frustration_total: number;
  timestamp: number;
}

export interface RagContextEvent {
  sources: string[];
  context_length: number;
  passive?: boolean;
  queued?: boolean;
  timestamp: number;
}

export interface UseCharlotteVoiceOptions extends CharlotteVoiceConfig {
  onTranscript?: (t: VoiceTranscript) => void;
  onRagContext?: (e: RagContextEvent) => void;
  onError?: (msg: string) => void;
  onEngagement?: (e: VoiceEngagement) => void;
}

export interface UseCharlotteVoiceReturn {
  isConnected: boolean;
  isActive: boolean;
  isMuted: boolean;
  isSpeaking: boolean;
  transcripts: VoiceTranscript[];
  stats: VoiceStats;
  engagement: VoiceEngagement | null;
  start: () => Promise<void>;
  stop: () => void;
  toggleMute: () => void;
  cancel: () => void;
  clearTranscripts: () => void;
  sendText: (text: string) => void;
  /** Whether the browser supports MediaStream (mic access) */
  isSupported: boolean;
}

// ── Audio Utilities ──────────────────────────────────────────────────

function resampleTo24k(inputBuffer: Float32Array, inputSampleRate: number): Int16Array {
  const ratio = inputSampleRate / 24000;
  const outputLength = Math.floor(inputBuffer.length / ratio);
  const output = new Int16Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const srcIdx = Math.floor(i * ratio);
    const sample = Math.max(-1, Math.min(1, inputBuffer[srcIdx]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useCharlotteVoice(options: UseCharlotteVoiceOptions): UseCharlotteVoiceReturn {
  const {
    apiUrl,
    wsUrl: wsUrlOverride,
    voice,
    instructions,
    ragCollection,
    onTranscript,
    onRagContext,
    onError,
    onEngagement,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcripts, setTranscripts] = useState<VoiceTranscript[]>([]);
  const [stats, setStats] = useState<VoiceStats>({
    audioChunksSent: 0,
    audioChunksReceived: 0,
    responsesCount: 0,
  });
  const [engagement, setEngagement] = useState<VoiceEngagement | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const isMutedRef = useRef(false);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);

  // Check browser support
  const isSupported =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof AudioContext !== 'undefined';

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const playNextChunk = useCallback(async () => {
    if (!audioCtxRef.current || audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return;
    }
    isPlayingRef.current = true;
    const chunk = audioQueueRef.current.shift()!;
    const pcm16 = new Int16Array(chunk);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7fff);
    }
    const buffer = audioCtxRef.current.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32, 0);
    const source = audioCtxRef.current.createBufferSource();
    source.buffer = buffer;
    source.connect(audioCtxRef.current.destination);
    source.onended = () => playNextChunk();
    source.start();
  }, []);

  const enqueueAudio = useCallback(
    (base64Audio: string) => {
      const binary = atob(base64Audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      audioQueueRef.current.push(bytes.buffer);
      if (!isPlayingRef.current) playNextChunk();
    },
    [playNextChunk],
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as Record<string, unknown>;
        switch (data.type) {
          case 'session_ready':
            setIsConnected(true);
            break;
          case 'audio':
            if (data.audio) {
              setIsSpeaking(true);
              setStats((prev) => ({
                ...prev,
                audioChunksReceived: prev.audioChunksReceived + 1,
              }));
              enqueueAudio(data.audio as string);
            }
            break;
          case 'transcript_done': {
            const t: VoiceTranscript = {
              id: generateId(),
              source: (data.source as 'user' | 'assistant') ?? 'assistant',
              text: (data.text as string) ?? '',
              timestamp: Date.now(),
            };
            setTranscripts((prev) => [...prev, t]);
            onTranscript?.(t);
            break;
          }
          case 'response_done':
            setIsSpeaking(false);
            setStats((prev) => ({ ...prev, responsesCount: prev.responsesCount + 1 }));
            break;
          case 'response_cancelled':
            setIsSpeaking(false);
            break;
          case 'reconnecting':
            setIsConnected(false);
            setIsSpeaking(false);
            break;
          case 'rag_context':
            onRagContext?.({
              sources: (data.sources as string[]) ?? [],
              context_length: (data.context_length as number) ?? 0,
              passive: data.passive as boolean | undefined,
              queued: data.queued as boolean | undefined,
              timestamp: Date.now(),
            });
            break;
          case 'engagement_signal': {
            const sig: VoiceEngagement = {
              signal: (data.signal as string) ?? '',
              register: (data.register as string) ?? '',
              satisfaction_total: (data.satisfaction_total as number) ?? 0,
              frustration_total: (data.frustration_total as number) ?? 0,
              timestamp: Date.now(),
            };
            setEngagement(sig);
            onEngagement?.(sig);
            break;
          }
          case 'error':
            onError?.((data.message as string) ?? 'Unknown error');
            break;
          case 'heartbeat':
          case 'pong':
            break;
        }
      } catch {
        // Ignore non-JSON messages
      }
    },
    [enqueueAudio, onTranscript, onRagContext, onError, onEngagement],
  );

  const buildWsUrl = useCallback(() => {
    let base = wsUrlOverride;
    if (!base) {
      base = apiUrl.replace(/^http/, 'ws') + '/api/voice/voicelive/ws';
    }
    const params = new URLSearchParams();
    if (voice) params.set('voice', voice);
    if (instructions) params.set('instructions', instructions);
    if (ragCollection) params.set('rag_collection', ragCollection);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [apiUrl, wsUrlOverride, voice, instructions, ragCollection]);

  const start = useCallback(async () => {
    if (isActive) return;
    if (!isSupported) {
      onError?.('Voice input is not supported in this browser.');
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: { ideal: 48000 },
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    micStreamRef.current = stream;

    const audioCtx = new AudioContext({ sampleRate: 48000 });
    audioCtxRef.current = audioCtx;

    const sourceNode = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    const url = buildWsUrl();
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setIsActive(true);
    ws.onclose = () => {
      setIsConnected(false);
      setIsActive(false);
      setIsSpeaking(false);
    };
    ws.onerror = () => onError?.('WebSocket connection failed');
    ws.onmessage = handleMessage;

    processor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (isMutedRef.current || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const pcm16 = resampleTo24k(input, audioCtx.sampleRate);
      const b64 = int16ToBase64(pcm16);
      ws.send(JSON.stringify({ type: 'audio', audio: b64 }));
      setStats((prev) => ({ ...prev, audioChunksSent: prev.audioChunksSent + 1 }));
    };

    sourceNode.connect(processor);
    processor.connect(audioCtx.destination);
  }, [isActive, isSupported, buildWsUrl, handleMessage, onError]);

  const stop = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsActive(false);
    setIsConnected(false);
    setIsSpeaking(false);
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => setIsMuted((prev) => !prev), []);

  const cancel = useCallback(() => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsSpeaking(false);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel' }));
    }
  }, []);

  const sendText = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && text.trim()) {
      wsRef.current.send(JSON.stringify({ type: 'text', text: text.trim() }));
    }
  }, []);

  const clearTranscripts = useCallback(() => setTranscripts([]), []);

  // Cleanup on unmount
  useEffect(() => () => stop(), [stop]);

  return {
    isConnected,
    isActive,
    isMuted,
    isSpeaking,
    transcripts,
    stats,
    engagement,
    start,
    stop,
    toggleMute,
    cancel,
    clearTranscripts,
    sendText,
    isSupported,
  };
}
